import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';
import CategoryPage from './CategoryPage';
import AsianFaq from '../faqs/AsianFaq';

function AsianCategory() {
  const { city_name, vehicle_type } = useContext(ConfigContext);
  const vehicleLowerCase = vehicle_type.toLowerCase();
  const { content: cms } = usePageContent('cuisine_asian');

  const MainText = (
    <>
      <h1>{cms?.main?.heading || `Asian food ${vehicleLowerCase}s in ${city_name}`}</h1>
      <CmsRichText
        html={cms?.main?.body}
        fallback={
          <p>Looking for bao, banh mi, Korean bowls, pho, or Thai curry? Browse { city_name } Asian food { vehicleLowerCase }s, check menus and schedules, or request catering for an event.</p>
        }
      />
    </>
  );

  const SecondaryText = (
    <>
      <h3 className='u-phosphate'>{cms?.secondary?.heading || 'What you’ll find'}</h3>
      <CmsRichText
        html={cms?.secondary?.body}
        fallback={
          <p>Asian covers a lot of ground here: Vietnamese banh mi and pho, Korean bowls and BBQ, Thai curries and noodles, Chinese dumplings, and fusion menus that mix them. Most { vehicleLowerCase }s build the menu around a rice or noodle base with a choice of protein, so a vegetarian order is usually easy. Check each menu for spice levels and dietary notes.</p>
        }
      />

      { city_name === 'Seattle' && !cms &&
        <>
          <h3 className='u-phosphate u-mt4'>Seattle’s Top Asian Food Trucks</h3>
          <p>Seattle has no shortage of incredible Asian food trucks, each offering something different. <Link to='/food-trucks/the-box-on-wheels'>The Box On Wheels</Link> specializes in Asian fusion, serving up dishes like pork belly bao, Kalbi yakiniku plates, and their signature Chop Chop Salad with a creamy sesame dressing. If you’re in the mood for a bite-sized snack, their mini bagel burger with hoisin and Sriracha ketchup is a must-try.</p>
          <p>For those who crave traditional flavors with a modern twist, <Link to='/food-trucks/chopstix'>Chopstix</Link> delivers. Their menu includes Peking duck tacos, Asian pulled pork sandwiches, and crispy nori fries dusted with seaweed flakes. If you’re looking for something hearty, their fried rice dishes come packed with pork, duck, Thai chicken, or tofu, making them a great option for a filling meal.</p>
          <p>If drinks are more your style, <Link to='/food-trucks/dreamy-drinks'>Dreamy Drinks</Link> has you covered. As Seattle’s first bubble tea food truck, they offer everything from classic milk teas to smoothies and espresso-based drinks, ensuring you get the perfect sweet sip to pair with your meal.</p>
          <p>For fans of fusion flavors, <Link to='/food-trucks/el-koreano'>El Koreano</Link> is the spot. This truck blends the best of Korean and Mexican cuisine, serving up dishes like bulgogi tortas, kimchi fried rice quesadillas, and pork bulgogi tacos with spicy mayo. If you’re feeling adventurous, their corn and cheese quesadilla with furikake fries is an unexpected but delicious combination.</p>
          <p>Vietnamese street food lovers should head straight to <Link to='/food-trucks/mami-tran'>Mami Tran</Link>, a food truck run by two sisters who honor their mother’s recipes. Their banh mi sandwiches, crispy pork egg rolls, and garlic noodles are packed with fresh herbs and bold flavors. For a lighter option, try their vermicelli rice noodle bowls, which come topped with tofu, chicken, or grilled pork.</p>
          <p>Hawaiian and Korean flavors come together at <Link to='/food-trucks/marination-mobile'>Marination Mobile</Link>, where dishes like Korean BBQ sliders, Hawaiian-style tacos, and sweet soy-glazed spam musubi make for a perfect meal on the go. Their food is built for easy eating, with handheld options that pack a punch of tropical and savory flavors.</p>
          <p>For a fully customizable experience, <Link to='/food-trucks/seoul-bowl-co'>Seoul Bowl Co.</Link> offers a build-your-own Korean BBQ bowl concept, allowing customers to mix and match proteins, sauces, and toppings for a unique meal every time. It’s an excellent way to explore the flavors of Korean BBQ while keeping things fresh and personalized.</p>
          <p>If you’re in the mood for Thai food, <Link to='/food-trucks/thai-u-up'>Thai-U-Up</Link> brings authentic street food flavors straight to Seattle. Their Khao Man Gai (Hainanese chicken rice) is a fan favorite, along with their rich curry noodles and crispy basil jalapeño prawns. They also offer plenty of vegetarian and gluten-free dishes, making them a great option for groups with diverse dietary needs.</p>
        </>
      }

      <h3 className='u-phosphate u-mt4'>{cms?.events?.heading || 'Asian catering for your event'}</h3>
      <CmsRichText
        html={cms?.events?.body}
        fallback={
          <p>Bowls and handhelds serve fast, and a mix-and-match menu covers picky eaters without a second caterer. Tell us your date, location, and guest count. We’ll find { vehicleLowerCase }s that fit.</p>
        }
      />
    </>
  );

  return (
    <CategoryPage
      foodName='Asian'
      foodCategory='asian'
      seoTitle={cms?.meta?.title || `Asian food ${vehicleLowerCase}s in ${city_name}`}
      seoDescription={cms?.meta?.description || `Find and book Asian food ${vehicleLowerCase}s in ${city_name}. Browse menus, compare quotes, and hire Asian food catering for your next event.`}
      seoCanonical='/asian-food-trucks'
      MainText={MainText}
      SecondaryText={SecondaryText}
      Faq={<AsianFaq />}
    />
  );
}

export default AsianCategory;
